import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Grid,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  ListAlt as ListAltIcon,
  PictureAsPdf as PdfIcon,
  DeleteOutline as DeleteIcon
} from '@mui/icons-material';
import {
  listCommercialInvoices,
  getCommercialInvoice,
  updateCommercialInvoiceItem,
  commercialInvoicePreviewUrl,
  commercialInvoicePdfUrl
} from '../../services/inventoryService';
import api from '../../config/api';
import { EntityChip } from '../ds';

/**
 * Commercial Invoices — list + per-line editor + HTML preview.
 * V1: HTML preview (browser-print to PDF). PDF generation (puppeteer) deferred.
 */
export default function CommercialInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editItems, setEditItems] = useState([]);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [pendingHeader, setPendingHeader] = useState(null); // debounced text patches

  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

  const load = async () => {
    try {
      setLoading(true);
      const r = await listCommercialInvoices();
      setInvoices(r.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSubsidiaries = async () => {
    try {
      const r = await api.get(`${API_BASE}/api/subsidiaries`);
      // Endpoint shape: array OR { success, subsidiaries }
      const arr = Array.isArray(r.data) ? r.data : (r.data?.subsidiaries || r.data?.data || []);
      setSubsidiaries(arr);
    } catch (e) { /* non-blocking */ }
  };

  useEffect(() => { load(); loadSubsidiaries(); }, []);

  // ?focus=<id> — Purchase Orders "Generate commercial invoice" lands here; open that invoice.
  useEffect(() => {
    const focusId = new URLSearchParams(window.location.search).get('focus');
    if (/^\d+$/.test(focusId || '')) openDetail(focusId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Header field patcher. When `debounce=true`, the local state updates
  // immediately for snappy typing, and `handleHeaderFlush()` (called onBlur)
  // sends one PUT with the accumulated patch. Non-text changes (select dropdown)
  // call this with `debounce=false` to write through immediately.
  const handleHeaderChange = (patch, debounce = false) => {
    setDetail(d => d ? { ...d, ...patch } : d);
    if (debounce) {
      setPendingHeader(p => ({ ...(p || {}), ...patch }));
    } else {
      api.put(`${API_BASE}/api/commercial-invoices/${detail.id}`, patch)
        .catch(e => setError(e.response?.data?.error || e.message));
    }
  };
  const handleHeaderFlush = async () => {
    if (!pendingHeader || !detail?.id) return;
    const patch = pendingHeader;
    setPendingHeader(null);
    try {
      await api.put(`${API_BASE}/api/commercial-invoices/${detail.id}`, patch);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const openDetail = async (id) => {
    try {
      setDetailLoading(true);
      setError(null); setDetailOpen(true);
      const r = await getCommercialInvoice(id);
      setDetail(r.data);
      setEditItems((r.data?.items || []).map(it => ({ ...it, _dirty: false })));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleItemChange = (idx, field, value) => {
    setEditItems(editItems.map((it, i) => i === idx ? { ...it, [field]: value, _dirty: true } : it));
  };

  const handleSaveItem = async (item) => {
    try {
      await updateCommercialInvoiceItem(detail.id, item.id, {
        description: item.description,
        hs_code: item.hs_code,
        country_of_origin: item.country_of_origin,
        quantity: item.quantity,
        unit_price: item.unit_price,
        net_weight_kg: item.net_weight_kg,
        gross_weight_kg: item.gross_weight_kg,
        cartons: item.cartons,
        per_case: item.per_case,
      });
      setSuccess(`Line ${item.line_number} saved`);
      // Reload detail
      const r = await getCommercialInvoice(detail.id);
      setDetail(r.data);
      setEditItems((r.data?.items || []).map(it => ({ ...it, _dirty: false })));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const previewInBrowser = (id, kind = 'invoice') => {
    // Backend serves HTML. Token is required so open via temporary fetch + blob URL.
    api.get(commercialInvoicePreviewUrl(id, kind), { responseType: 'text' })
      .then(res => {
        const blob = new Blob([res.data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
      })
      .catch(e => setError(e.message));
  };

  // Download PDF — backend renders the same HBS template via headless Chrome
  // and streams a real .pdf with Content-Disposition attachment. We fetch
  // as a blob (so the auth header attaches) and trigger a download.
  const downloadPDF = (id, kind = 'invoice') => {
    api.get(commercialInvoicePdfUrl(id, kind), { responseType: 'blob' })
      .then(res => {
        // Filename hint: backend sets Content-Disposition with the invoice_number.
        const cd = res.headers?.['content-disposition'] || '';
        const m = cd.match(/filename="?([^";]+)"?/);
        const filename = m ? m[1] : `${kind === 'packing-list' ? 'PackingList' : 'CommercialInvoice'}-${id}.pdf`;
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(e => setError(e.response?.data?.error || e.message));
  };

  // Hard-delete a CI. CI is a customs doc — the confirm is load-bearing
  // (operators sometimes click delete from the list expecting "void" semantics).
  // Backend removes both the header and the items rows; if the parent transfer
  // still exists, it can re-generate a fresh CI on demand. Requested 2026-05-21.
  const handleDelete = async (inv) => {
    const label = inv.invoice_number || `#${inv.id}`;
    const shortLabel = String(label).replace(/^([A-Z]+-)0+(\d)/i, '$1$2');
    if (!window.confirm(
      `Delete commercial invoice ${shortLabel}?\n\n` +
      `This permanently removes the CI header + line items. If the parent transfer still exists, ` +
      `you can re-generate a fresh CI from it later. Cannot be undone.`
    )) return;
    try {
      await api.delete(`${API_BASE}/api/commercial-invoices/${inv.id}`);
      setSuccess(`Deleted ${shortLabel}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Commercial Invoices</Typography>
          <Typography variant="body2" color="textSecondary">
            Customs-ready invoices generated from cross-border warehouse transfers. Use "Generate Commercial Invoice" on a cross-border transfer to create.
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Transfer</TableCell>
                  <TableCell>From → To</TableCell>
                  <TableCell>Seller (Subsidiary)</TableCell>
                  <TableCell align="right">Currency / Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center">
                    <Typography color="textSecondary" sx={{ py: 2 }}>
                      No commercial invoices yet. Generate one from a cross-border transfer.
                    </Typography>
                  </TableCell></TableRow>
                ) : invoices.map(inv => (
                  <TableRow key={inv.id} hover>
                    <TableCell><strong>{String(inv.invoice_number || '').replace(/^([A-Z]+-)0+(\d)/i, '$1$2')}</strong></TableCell>
                    <TableCell>{inv.invoice_date}</TableCell>
                    <TableCell>{inv.transfer_number || `#${inv.transfer_id}`}</TableCell>
                    <TableCell>
                      <EntityChip code={inv.from_warehouse_code} name={inv.from_warehouse_name} />
                      <Typography component="span" sx={{ mx: 0.5, color: 'text.disabled' }}>→</Typography>
                      <EntityChip code={inv.to_warehouse_code} name={inv.to_warehouse_name} />
                    </TableCell>
                    <TableCell>{inv.seller_subsidiary_name || inv.seller_subsidiary || (inv.seller_subsidiary_id ? `Subsidiary #${inv.seller_subsidiary_id}` : 'Unmapped')}</TableCell>
                    <TableCell align="right">{inv.currency} {Number(inv.total).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={inv.status} size="small"
                        color={inv.status === 'issued' ? 'success' : inv.status === 'cancelled' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openDetail(inv.id)} title="Edit lines">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => previewInBrowser(inv.id, 'invoice')} title="View Invoice">
                        <DescriptionIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => previewInBrowser(inv.id, 'packing-list')} title="View Packing List">
                        <ListAltIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => downloadPDF(inv.id, 'invoice')} title="Download Invoice as PDF">
                        <PdfIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(inv)} title="Delete CI">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detail ? `${String(detail.invoice_number || '').replace(/^([A-Z]+-)0+(\d)/i, '$1$2')} — ${detail.transfer_number}` : 'Loading…'}
        </DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          {detailLoading ? <CircularProgress /> : detail ? (
            <Box>
              {/* Seller + Consignee overrides — needed when the auto-resolved
                  subsidiary is wrong (e.g. brand-specific seller) or when the
                  to-warehouse is virtual ('GIT' = Goods In Transit) and customs
                  needs the actual final destination. */}
              <Box sx={{ p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Override seller + consignee (optional)</Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Selling Subsidiary</InputLabel>
                      <Select
                        label="Selling Subsidiary"
                        value={detail.seller_subsidiary_id || ''}
                        onChange={(e) => handleHeaderChange({ seller_subsidiary_id: Number(e.target.value) || 0 })}
                      >
                        {subsidiaries.map(s => (
                          <MenuItem key={s.id} value={s.id}>{s.legal_name || s.name} ({s.country})</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Consignee name (override)"
                      value={detail.override_consignee_name || ''}
                      onChange={(e) => handleHeaderChange({ override_consignee_name: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder={detail.to_warehouse_name || ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Consignee address line 1 (override)"
                      value={detail.override_consignee_address_line1 || ''}
                      onChange={(e) => handleHeaderChange({ override_consignee_address_line1: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder={detail.to_address_line1 || ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField size="small" fullWidth label="City (override)"
                      value={detail.override_consignee_city || ''}
                      onChange={(e) => handleHeaderChange({ override_consignee_city: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder={detail.to_city || ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField size="small" fullWidth label="Country (override)"
                      value={detail.override_consignee_country || ''}
                      onChange={(e) => handleHeaderChange({ override_consignee_country: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder={detail.to_country || ''}
                    />
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Leave blank to use the auto-resolved value. Use overrides for GIT / virtual-warehouse transfers, or when the customs-recognised consignee differs from the next inventory destination.
                </Typography>
                {/* Sender contact — the person dispatching THIS shipment varies by who
                    sends it, so allow a per-invoice override of the Seller / Exporter
                    contact. Placeholders show the entity default; blank = use it. */}
                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mt: 1.5, display: 'block' }}>
                  Sender contact — prints in the Seller / Exporter block. Leave blank to use the entity default.
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" fullWidth label="Contact name (override)"
                      value={detail.override_seller_contact_name || ''}
                      onChange={(e) => handleHeaderChange({ override_seller_contact_name: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder="e.g. Jane Doe"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" fullWidth label="Contact phone (override)"
                      value={detail.override_seller_contact_phone || ''}
                      onChange={(e) => handleHeaderChange({ override_seller_contact_phone: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder={detail.seller_phone || 'Tel'}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" fullWidth label="Contact email (override)"
                      value={detail.override_seller_contact_email || ''}
                      onChange={(e) => handleHeaderChange({ override_seller_contact_email: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder={detail.seller_email || 'Email'}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" fullWidth label="Incoterms"
                      value={detail.incoterms || ''}
                      onChange={(e) => handleHeaderChange({ incoterms: e.target.value.toUpperCase() }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder="e.g. FCA, DAP, FOB, EXW, DDP"
                      helperText="Commercial-invoice Incoterms (3-letter code + named place). Renders on the printed PDF."
                      FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" fullWidth label="Port of Loading"
                      value={detail.port_of_loading || ''}
                      onChange={(e) => handleHeaderChange({ port_of_loading: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder="e.g. Shanghai, Sydney"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField size="small" fullWidth label="Port of Discharge"
                      value={detail.port_of_discharge || ''}
                      onChange={(e) => handleHeaderChange({ port_of_discharge: e.target.value }, true)}
                      onBlur={() => handleHeaderFlush()}
                      placeholder="e.g. Los Angeles, Rotterdam"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Seller (rendered)</Typography>
                  <Typography>{detail.seller_legal_name || '—'} ({detail.seller_country})</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Consignee (rendered)</Typography>
                  <Typography>
                    {detail.override_consignee_name || detail.to_warehouse_name}
                    {' '}({detail.override_consignee_country || detail.to_country})
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="textSecondary">Currency</Typography>
                  <Typography>{detail.currency}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="textSecondary">Subtotal</Typography>
                  <Typography>{Number(detail.subtotal).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="textSecondary">Freight + Customs</Typography>
                  <Typography>{Number((detail.freight_cost || 0) + (detail.customs_duty || 0)).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="caption" color="textSecondary"><strong>Total</strong></Typography>
                  <Typography fontWeight="bold">{Number(detail.total).toFixed(2)}</Typography>
                </Grid>
              </Grid>
              <Typography variant="h6" gutterBottom>Line Items</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>HS Code</TableCell>
                      <TableCell>COO</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Cartons</TableCell>
                      <TableCell align="right">Gross kg</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editItems.map((it, idx) => (
                      <TableRow key={it.id} sx={{ bgcolor: it._dirty ? 'action.hover' : 'inherit' }}>
                        <TableCell>{it.line_number}</TableCell>
                        <TableCell>{it.sku}</TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" fullWidth value={it.description || ''}
                            onChange={(e) => handleItemChange(idx, 'description', e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" value={it.hs_code || ''}
                            onChange={(e) => handleItemChange(idx, 'hs_code', e.target.value)}
                            sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" variant="standard" value={it.country_of_origin || ''}
                            onChange={(e) => handleItemChange(idx, 'country_of_origin', e.target.value)}
                            sx={{ width: 100 }} />
                        </TableCell>
                        <TableCell align="right">{Number(it.quantity).toFixed(0)}</TableCell>
                        <TableCell align="right">{Number(it.unit_price).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <TextField size="small" variant="standard" type="number" value={it.cartons || 0}
                            onChange={(e) => handleItemChange(idx, 'cartons', parseInt(e.target.value) || 0)}
                            sx={{ width: 60 }} inputProps={{ style: { textAlign: 'right' } }} />
                        </TableCell>
                        <TableCell align="right">
                          <TextField size="small" variant="standard" type="number" value={it.gross_weight_kg || 0}
                            onChange={(e) => handleItemChange(idx, 'gross_weight_kg', parseFloat(e.target.value) || 0)}
                            sx={{ width: 70 }} inputProps={{ style: { textAlign: 'right' } }} />
                        </TableCell>
                        <TableCell>
                          {it._dirty && (
                            <IconButton size="small" color="primary" onClick={() => handleSaveItem(it)}>
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box mt={2} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button startIcon={<OpenInNewIcon />} onClick={() => previewInBrowser(detail.id, 'invoice')}>
                  View Invoice HTML
                </Button>
                <Button startIcon={<OpenInNewIcon />} onClick={() => previewInBrowser(detail.id, 'packing-list')}>
                  View Packing List HTML
                </Button>
                <Button startIcon={<PdfIcon />} color="error" variant="contained"
                  onClick={() => downloadPDF(detail.id, 'invoice')}>
                  Download Invoice PDF
                </Button>
                <Button startIcon={<PdfIcon />} color="error" variant="outlined"
                  onClick={() => downloadPDF(detail.id, 'packing-list')}>
                  Download Packing List PDF
                </Button>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
